import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError, UploadProgress } from '../api/client';
import { videoGenApi, type PageParams } from '../api/endpoints';
import type {
  Id,
  VideoGenServiceStatus,
  VideoGenTab,
  VideoGeneration,
  VideoGenerationPage,
} from '../types/api';
import { queryKeys } from './keys';

const RUNNING_POLL_MS = 4000;
// Finished generations are still refetched: their presigned video links expire.
const IDLE_POLL_MS = 60_000;
const STATUS_STALE_MS = 60_000;

export const VIDEO_GEN_MAX_MB = 10;
export const VIDEO_GEN_MAX_PROMPT_CHARS = 2000;
export const VIDEO_GEN_MIN_SIDE_PX = 300;
export const VIDEO_GEN_MAX_SIDE_RATIO = 2.5;

export function useVideoGenStatus(): UseQueryResult<VideoGenServiceStatus, ApiError> {
  return useQuery<VideoGenServiceStatus, ApiError>({
    queryKey: queryKeys.videoGenStatus,
    queryFn: () => videoGenApi.status(),
    staleTime: STATUS_STALE_MS,
  });
}

export function useVideoGenerations(
  params: PageParams = {},
): UseQueryResult<VideoGenerationPage, ApiError> {
  return useQuery<VideoGenerationPage, ApiError>({
    queryKey: queryKeys.videoGenList(params),
    queryFn: () => videoGenApi.list(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((g) => g.status === 'running')
        ? RUNNING_POLL_MS
        : IDLE_POLL_MS,
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: queryKeys.videoGenRoot });
}

export function useStartVideoGen(onProgress?: (progress: UploadProgress) => void) {
  const refresh = useRefresh();
  return useMutation<
    VideoGeneration,
    ApiError,
    { tab: VideoGenTab; prompt: string; file: File; endFile: File | null }
  >({
    mutationFn: ({ tab, prompt, file, endFile }) =>
      videoGenApi.create(tab, prompt, file, endFile, onProgress),
    retry: false,
    onSettled: refresh,
  });
}

export function useRetryVideoGen() {
  const refresh = useRefresh();
  return useMutation<VideoGeneration, ApiError, Id>({
    mutationFn: (generationId) => videoGenApi.retry(generationId),
    retry: false,
    onSettled: refresh,
  });
}
